// La page d'une Page : en-tête, abonnés, publications, et sa gestion.
//
// Ce qui la sépare d'un groupe tient en une ligne : **seuls ses administrateurs
// y publient**. Un groupe est un lieu d'échange, une page est une voix. C'est
// aussi pourquoi la politique de sponsoring n'apparaît qu'ici, et seulement pour
// ceux qui administrent — c'est à eux qu'elle s'adresse.
//
// L'auteur humain reste enregistré sur chaque publication même si elle paraît au
// nom de la page : la modération doit toujours pouvoir savoir qui a écrit.

import { useEffect, useState } from "react";
import Store from "../../store.js";
import { toast, confirmer } from "../../ui.js";
import { estModerateur } from "../../views/moderation.js";
import { categorieDe } from "../../espaces-meta.js";
import { gererPage } from "../../views/page-gerer.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import ComposeurEspace from "../ComposeurEspace.jsx";
import ListePublications from "../ListePublications.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement } from "../hooks.js";

/* Combien d'abonnés on montre avant de proposer la liste entière.
   Six : trois lignes sur un téléphone, qui se voient d'un coup d'œil. Au-delà,
   la liste repoussait les PUBLICATIONS hors de l'écran — sur une page de
   cinquante abonnés, il fallait faire défiler cinquante lignes avant d'arriver
   à ce que la page a écrit. Or c'est pour cela qu'on ouvre une page. */
const APERCU_ABONNES = 6;

function Abonnes({ gens }) {
  const [tout, setTout] = useState(false);
  if (!gens.length) return <Vide symbole="👥" titre="Aucun abonné" />;

  // Les administrateurs d'abord : ce sont eux qu'on cherche quand on veut
  // savoir qui tient la page.
  const ordonnes = [...gens].sort(
    (a, b) => (b.role === "admin" ? 1 : 0) - (a.role === "admin" ? 1 : 0)
  );
  const montres = tout ? ordonnes : ordonnes.slice(0, APERCU_ABONNES);
  const reste = gens.length - montres.length;

  return (
    <>
      <ListeAbonnes gens={montres} />
      {reste > 0 && (
        <button className="btn btn--fantome page__voir-abonnes" type="button" onClick={() => setTout(true)}>
          Voir les {gens.length} abonnés
        </button>
      )}
    </>
  );
}

function ListeAbonnes({ gens }) {
  return (
    <div className="grille-membres">
      {gens.map((u) => (
        <a className="membre" href={`#/profil/${u.uid}`} key={u.uid}>
          <span className="membre__qui">
            <Avatar membre={u} taille={44} />
            <span>
              <strong>
                {u.nom}
                <Certifie membre={u} taille={13} />
              </strong>
              <span className="petit texte-doux">
                {u.role === "admin" ? "Administrateur" : u.eglise || u.ville || "Abonné"}
              </span>
            </span>
          </span>
        </a>
      ))}
    </div>
  );
}

export default function Page({ ctx, params }) {
  const id = params.id;
  const [etat, setEtat] = useState({ phase: "chargement" });
  const abonnes = useAbonnement((cb) => Store.pages.subscribeAbonnes(id, cb), [], [id]);

  useEffect(() => {
    let vivant = true;
    setEtat({ phase: "chargement" });
    Store.pages
      .get(id)
      .then((pg) => vivant && setEtat(pg ? { phase: "prete", pg } : { phase: "introuvable" }))
      .catch(() => vivant && setEtat({ phase: "introuvable" }));
    return () => {
      vivant = false;
    };
  }, [id]);

  if (etat.phase === "chargement")
    return (
      <div className="colonne">
        <Chargement texte="Chargement de la page…" />
      </div>
    );

  if (etat.phase === "introuvable")
    return (
      <div className="colonne">
        <Vide symbole="📄" titre="Page introuvable" texte="Elle a peut-être été supprimée." />
      </div>
    );

  const pg = etat.pg;
  const c = categorieDe(pg.categorie);
  const moi = abonnes.find((x) => x.uid === ctx.moi.uid);
  const admin = moi?.role === "admin";
  const abonne = Boolean(moi);

  const logo = pg.image ? <img src={pg.image} alt="" /> : c.icone;

  const supprimer = async () => {
    if (
      await confirmer("Supprimer cette page ?", "La page et toutes ses publications seront perdues.", "Supprimer")
    ) {
      await Store.pages.supprimer(id);
      toast("Page supprimée");
      location.hash = "#/pages";
    }
  };

  return (
    <div className="colonne">
      <section className="groupe-tete">
        <div
          className="profil__couverture"
          style={pg.couverture ? { backgroundImage: `url('${pg.couverture}')` } : undefined}
        >
          <span className="page-logo">{logo}</span>
        </div>
        <div className="groupe-tete__corps">
          <h1>
            {pg.nom}
            <Certifie membre={pg} taille={22} />
          </h1>
          <p className="petit texte-doux">
            {c.label} · {abonnes.length} abonné{abonnes.length > 1 ? "s" : ""}
          </p>
          {pg.description && (
            <p className="groupe-tete__desc">
              <TexteRiche texte={pg.description} />
            </p>
          )}
          <div className="profil__actions">
            {admin ? (
              <>
                <button
                  className="btn btn--gris"
                  onClick={() => gererPage(pg, id, (maj) => setEtat({ phase: "prete", pg: maj }))}
                >
                  <Icone nom="appareil" taille={17} /> Gérer la page
                </button>
                <button
                  className="btn btn--gris btn--carre"
                  aria-label="Supprimer la page"
                  onClick={supprimer}
                >
                  <Icone nom="fermer" taille={18} />
                </button>
              </>
            ) : abonne ? (
              <button
                className="btn btn--gris"
                onClick={async () => {
                  await Store.pages.nePlusSuivre(id);
                  toast("Désabonné");
                }}
              >
                ✓ Abonné
              </button>
            ) : (
              <button
                className="btn btn--primaire"
                onClick={async () => {
                  try {
                    await Store.pages.suivre(id);
                    toast(`Abonné à ${pg.nom}`);
                  } catch (e) {
                    toast(e.message || "Abonnement impossible", "erreur");
                  }
                }}
              >
                <Icone nom="plus" taille={17} /> S'abonner
              </button>
            )}
            {/* Une page se certifie comme un membre — et pas par celui qui
                l'administre : il suffirait sinon de créer une page pour se
                déclarer authentique. Les règles du serveur le disent aussi. */}
            {estModerateur(ctx.moi) && (
              <button
                className="btn btn--gris"
                onClick={async () => {
                  const oui = !pg.certifie;
                  if (
                    !oui &&
                    !(await confirmer("Retirer la certification ?", `Le sceau disparaîtra du nom de ${pg.nom}.`, "Retirer"))
                  )
                    return;
                  try {
                    await Store.moderation.certifierPage(id, oui);
                    setEtat({ phase: "prete", pg: { ...pg, certifie: oui || undefined } });
                    toast(oui ? "Page certifiée ✔️" : "Certification retirée");
                  } catch (e) {
                    toast(e.message || "Action impossible", "erreur");
                  }
                }}
              >
                {pg.certifie ? "Retirer la certification" : "Certifier cette page"}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Seuls les administrateurs publient : c'est ce qui fait une page. */}
      {admin && (
        <ComposeurEspace
          ctx={ctx}
          invite={`Publier en tant que ${pg.nom}…`}
          succes="Publié au nom de la page 🙌"
          /* Une page annonce un culte, une convention, un concert : cela ne se
             raconte pas sans image. Les groupes gardent leur champ de texte. */
          avecMedia
          champsEspace={{
            pageId: id,
            pageNom: pg.nom,
            pageImage: pg.image || "",
          }}
        />
      )}

      <h2 className="titre-section">Abonnés</h2>
      <Abonnes gens={abonnes} />

      {/* Les règles de mise en avant ne concernent que ceux qui administrent. */}
      {admin && (
        <a className="carte lien-politique" href="#/sponsoring">
          <Icone nom="options" taille={20} />
          <span>
            <strong>Politique de sponsoring</strong>
            <span className="petit texte-doux">À lire avant de demander une mise en avant.</span>
          </span>
        </a>
      )}

      <h2 className="titre-section">Publications</h2>
      <ListePublications
        ctx={ctx}
        chargerPage={(o) => Store.posts.page({ ...o, pageId: id })}
        abonnerRecentes={(cb) => Store.posts.subscribePage(id, cb)}
        vide={["📄", "Aucune publication", "Cette page n'a encore rien publié."]}
        deps={[id]}
      />
    </div>
  );
}
